/**
 * X01Game - Pantalla principal del juego de dardos X01
 * 501 Dart League Web
 * 
 * Replica la lógica del GameScreen.tsx de la app móvil:
 * - Marcador con puntuación restante
 * - Teclado numérico para entrada de puntos
 * - Historial de rondas por jugador
 * - Sugerencias de checkout (PDC)
 * - Promedios de 3 dardos en tiempo real
 * - Modal de dardos de cierre (1, 2 o 3)
 * - Modal de resultado final
 * - Deshacer última tirada
 * - Soporte de teclado físico
 * - Alternancia de turno por leg
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import { isValidMove } from './utils/gameUtils';
import TrainingMatchResultModal from './TrainingMatchResultModal';
import ScoreCelebrationBanner from './ScoreCelebrationBanner';
import {
  createEmptyMatchAdvancedStats,
  normalizeMatchAdvancedStats,
  registerAdvancedTurn,
} from './utils/leagueMatchStats';
import {
  getScoreCelebrationType,
  SCORE_CELEBRATION_DURATION_MS,
  shouldDelayResultModalForCelebration,
} from './utils/scoreCelebration';
import './styles/index.css';
import './styles/x01game.css';
import SponsorsStrip from './SponsorsStrip';

const EMPTY_RESULT_MODAL = {
  visible: false,
  data: null,
  pending: false,
};

// ========== MODALES ==========

const DardosCierreModal = ({ visible, closingScore, onSelect }) => {
  if (!visible) return null;

  const canOneD = closingScore <= 50 && closingScore % 2 === 0;
  const canTwoD = closingScore <= 110;

  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-dardos-modal">
        <h2>¡Has cerrado!</h2>
        <p className="x01-modal-subtitle">
          Cerraste <strong>{closingScore}</strong>. ¿Con cuántos dardos?
        </p>
        <div className="x01-dardos-options">
          <button
            className={`x01-dardo-btn ${!canOneD ? 'disabled' : ''}`}
            onClick={() => canOneD && onSelect(1)}
            disabled={!canOneD}
          >
            <span className="x01-dardo-num">1</span>
            <span className="x01-dardo-label">dardo</span>
            {!canOneD && <span className="x01-dardo-impossible">Imposible</span>}
          </button>
          <button
            className={`x01-dardo-btn ${!canTwoD ? 'disabled' : ''}`}
            onClick={() => canTwoD && onSelect(2)}
            disabled={!canTwoD}
          >
            <span className="x01-dardo-num">2</span>
            <span className="x01-dardo-label">dardos</span>
            {!canTwoD && <span className="x01-dardo-impossible">Imposible</span>}
          </button>
          <button className="x01-dardo-btn" onClick={() => onSelect(3)}>
            <span className="x01-dardo-num">3</span>
            <span className="x01-dardo-label">dardos</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ visible, title, message, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-confirm-modal">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="x01-confirm-actions">
          <button className="x01-result-btn secondary" onClick={onCancel}>Cancelar</button>
          <button className="x01-result-btn primary" onClick={onConfirm}>Confirmar</button>
        </div>
      </div>
    </div>
  );
};

// ========== COMPONENTE PRINCIPAL ==========

const X01Game = ({ config, onBack }) => {
  const { playerCount, gameMode, legsToWin, player1Name, player2Name } = config;
  const playerNames = useMemo(() => 
    playerCount === 1 ? [player1Name] : [player1Name, player2Name],
    [playerCount, player1Name, player2Name]
  );

  // Estado del juego
  const [scores, setScores] = useState(() => Array(playerCount).fill(gameMode));
  const [matchesWon, setMatchesWon] = useState(() => Array(playerCount).fill(0));
  const [activePlayer, setActivePlayer] = useState(0);
  const [input, setInput] = useState('');
  const [dartsThrown, setDartsThrown] = useState(() => Array(playerCount).fill(0));
  const [throws, setThrows] = useState(() => Array.from({ length: playerCount }, () => []));
  const [count180s, setCount180s] = useState(() => Array(playerCount).fill(0));
  const [moveHistory, setMoveHistory] = useState([]);
  const [matchCount, setMatchCount] = useState(0);
  const [firstStarter, setFirstStarter] = useState(0);
  const [advancedStats, setAdvancedStats] = useState(() => createEmptyMatchAdvancedStats());
  const [scoreCelebration, setScoreCelebration] = useState(null);

  // Promedios: acumulado por leg
  const [legAverages, setLegAverages] = useState([]);

  // Ref para auto-scroll del panel de rondas
  const roundsPanelRef = useRef(null);
  const mobileScrollRefs = useRef([]);
  const scoresDisplayRef = useRef(null);
  const celebrationTimerRef = useRef(null);
  const resultModalTimerRef = useRef(null);

  // Modales
  const [dardosModal, setDardosModal] = useState({ visible: false, closingScore: 0 });
  const [pendingWin, setPendingWin] = useState(null);
  const [resultModal, setResultModal] = useState(EMPTY_RESULT_MODAL);
  const [confirmModal, setConfirmModal] = useState({ visible: false, title: '', message: '', onConfirm: null });
  const [alertMsg, setAlertMsg] = useState(null);

  // Calcular promedios en tiempo real
  const currentAverages = useMemo(() => {
    return scores.map((_, i) => {
      const totalPoints = throws[i].reduce((sum, t) => sum + t, 0);
      const totalDarts = dartsThrown[i];
      if (totalDarts === 0) return 0;
      return (totalPoints / totalDarts) * 3;
    });
  }, [scores, throws, dartsThrown]);

  // Número máximo de rondas (para renderizar la tabla)
  const maxRounds = useMemo(() => Math.max(...throws.map(t => t.length), 0), [throws]);

  // Auto-scroll del panel de rondas (desktop: vertical, mobile: horizontal por jugador)
  useEffect(() => {
    // Desktop: scroll vertical
    const el = roundsPanelRef.current;
    if (el) {
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    }
    // Mobile: scroll horizontal de cada fila de jugador
    mobileScrollRefs.current.forEach(ref => {
      if (ref) {
        requestAnimationFrame(() => { ref.scrollLeft = ref.scrollWidth; });
      }
    });
  }, [throws]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
    scoresDisplayRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
  }, []);

  // Alert auto-dismiss
  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => setAlertMsg(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  const clearCelebrationTimers = useCallback(() => {
    if (celebrationTimerRef.current) {
      clearTimeout(celebrationTimerRef.current);
      celebrationTimerRef.current = null;
    }

    if (resultModalTimerRef.current) {
      clearTimeout(resultModalTimerRef.current);
      resultModalTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearCelebrationTimers();
  }, [clearCelebrationTimers]);

  const triggerScoreCelebration = useCallback((points, didCheckout) => {
    const celebrationType = getScoreCelebrationType(points, didCheckout);

    if (!celebrationType) {
      return;
    }

    if (celebrationTimerRef.current) {
      clearTimeout(celebrationTimerRef.current);
    }

    setScoreCelebration(celebrationType);
    celebrationTimerRef.current = setTimeout(() => {
      setScoreCelebration(null);
      celebrationTimerRef.current = null;
    }, SCORE_CELEBRATION_DURATION_MS);
  }, []);

  const openResultModal = useCallback((data, points) => {
    if (resultModalTimerRef.current) {
      clearTimeout(resultModalTimerRef.current);
      resultModalTimerRef.current = null;
    }

    if (shouldDelayResultModalForCelebration(points)) {
      setResultModal({ visible: false, data, pending: true });
      resultModalTimerRef.current = setTimeout(() => {
        setResultModal({ visible: true, data, pending: false });
        resultModalTimerRef.current = null;
      }, SCORE_CELEBRATION_DURATION_MS);
      return;
    }

    setResultModal({ visible: true, data, pending: false });
  }, []);

  // Teclado físico
  useEffect(() => {
    const handleKeyDown = (e) => {
      // No procesar si hay modal abierto
      if (dardosModal.visible || resultModal.visible || resultModal.pending || confirmModal.visible) return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleNumpad(e.key);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleOk();
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleClear();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
      } else if (e.key === 'z' && e.ctrlKey) {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // ========== LÓGICA DEL JUEGO ==========

  const handleNumpad = useCallback((digit) => {
    if (resultModal.pending) return;

    setInput(prev => {
      if (prev.length >= 3) return prev;
      return prev + digit;
    });
  }, [resultModal.pending]);

  const handleClear = useCallback(() => {
    if (resultModal.pending) return;

    if (input === '') {
      // BUST: 0 puntos, pasa turno, cuenta 3 dardos
      processThrow(0);
    } else {
      setInput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, resultModal.pending]);

  const handleOk = useCallback(() => {
    if (resultModal.pending || input === '') return;
    const points = parseInt(input, 10);
    processThrow(points);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, resultModal.pending]);

  const processThrow = (points) => {
    const currentScore = scores[activePlayer];

    // Validar puntuación 0-180
    if (points < 0 || points > 180) {
      setAlertMsg('Puntuación inválida. Máximo 180.');
      setInput('');
      return;
    }

    // Validar movimiento
    if (!isValidMove(currentScore, points)) {
      const newScore = currentScore - points;
      if (newScore < 0) {
        setAlertMsg(`¡BUST! No puedes restar ${points} a ${currentScore}.`);
      } else if (newScore === 1) {
        setAlertMsg('¡BUST! No puedes quedarte en 1.');
      }
      setInput('');
      return;
    }

    // Guardar estado para deshacer (máx 5)
    setMoveHistory(prev => {
      const entry = {
        scores: [...scores],
        activePlayer,
        dartsThrown: [...dartsThrown],
        throws: throws.map(t => [...t]),
        input,
        count180s: [...count180s],
        advancedStats: normalizeMatchAdvancedStats(advancedStats),
      };
      const newHistory = [...prev, entry];
      return newHistory.length > 5 ? newHistory.slice(-5) : newHistory;
    });

    const newScore = currentScore - points;
    const didCheckout = newScore === 0;
    const nextAdvancedStats = registerAdvancedTurn(
      advancedStats,
      activePlayer,
      currentScore,
      points,
      didCheckout,
    );

    if (points === 180) {
      setCount180s(prev => {
        const n = [...prev];
        n[activePlayer] += 1;
        return n;
      });
    }

    setAdvancedStats(nextAdvancedStats);
    triggerScoreCelebration(points, didCheckout);

    // Registrar tirada
    const newThrows = throws.map(t => [...t]);
    newThrows[activePlayer].push(points);
    setThrows(newThrows);

    // Si cerró (score = 0)
    if (didCheckout) {
      setScores(prev => {
        const n = [...prev];
        n[activePlayer] = 0;
        return n;
      });

      // Decidir si mostrar modal de dardos de cierre
      if (currentScore > 110) {
        // Auto 3 dardos
        processWinWithDarts(3, points, newThrows, nextAdvancedStats);
      } else {
        // Mostrar modal
        setPendingWin({ points, newThrows, advancedStats: nextAdvancedStats });
        setDardosModal({ visible: true, closingScore: currentScore });
      }
      setInput('');
      return;
    }

    // Actualizar puntuación y pasar turno
    setScores(prev => {
      const n = [...prev];
      n[activePlayer] = newScore;
      return n;
    });

    const newDarts = [...dartsThrown];
    newDarts[activePlayer] += 3;
    setDartsThrown(newDarts);

    // Pasar al siguiente jugador
    if (playerCount > 1) {
      setActivePlayer(prev => (prev + 1) % playerCount);
    }
    setInput('');
  };

  const processWinWithDarts = (closingDarts, points, currentThrows, finalAdvancedStats) => {
    // Dardos del último round del ganador
    const winnerDarts = [...dartsThrown];
    winnerDarts[activePlayer] += closingDarts;
    setDartsThrown(winnerDarts);

    // Calcular promedio de este leg para cada jugador
    const legAvgs = scores.map((_, i) => {
      const playerThrows = currentThrows[i];
      const totalPoints = playerThrows.reduce((sum, t) => sum + t, 0);
      const totalDarts = i === activePlayer ? winnerDarts[i] : dartsThrown[i];
      if (totalDarts === 0) return 0;
      return (totalPoints / totalDarts) * 3;
    });

    const newLegAverages = [...legAverages, legAvgs];
    setLegAverages(newLegAverages);

    // Incrementar legs ganados
    const newMatchesWon = [...matchesWon];
    newMatchesWon[activePlayer]++;
    setMatchesWon(newMatchesWon);

    // Calcular promedios totales (media de todos los legs)
    const totalAverages = scores.map((_, i) => {
      const allAvgs = newLegAverages.map(la => la[i]);
      if (allAvgs.length === 0) return 0;
      return allAvgs.reduce((sum, a) => sum + a, 0) / allAvgs.length;
    });

    // ¿Partida terminada?
    const isMatchOver = newMatchesWon[activePlayer] >= legsToWin;

    const playerStats = playerNames.map((name, index) => ({
      name,
      gamesWon: newMatchesWon[index],
      average: legAvgs[index],
      totalAverage: totalAverages[index],
      count180s: count180s[index] || 0,
      advancedStats: index === 0 ? finalAdvancedStats.jugador1 : finalAdvancedStats.jugador2,
    }));

    openResultModal({
      winner: activePlayer,
      isMatchOver,
      playerStats,
    }, points);

    setDardosModal({ visible: false, closingScore: 0 });
    setPendingWin(null);
  };

  const handleDardosCierre = (numDarts) => {
    if (pendingWin) {
      processWinWithDarts(numDarts, pendingWin.points, pendingWin.newThrows, pendingWin.advancedStats);
    }
  };

  const handleNextLeg = () => {
    clearCelebrationTimers();
    setScoreCelebration(null);
    setResultModal(EMPTY_RESULT_MODAL);
    const nextMatchCount = matchCount + 1;
    setMatchCount(nextMatchCount);

    // Reset para siguiente leg
    setScores(Array(playerCount).fill(gameMode));
    setDartsThrown(Array(playerCount).fill(0));
    setThrows(Array.from({ length: playerCount }, () => []));
    setInput('');
    setMoveHistory([]);

    // Alternar quién empieza
    const nextStarter = (firstStarter + nextMatchCount) % playerCount;
    setActivePlayer(nextStarter);
  };

  const handleRematch = () => {
    // Reset completo
    clearCelebrationTimers();
    setScoreCelebration(null);
    setResultModal(EMPTY_RESULT_MODAL);
    setScores(Array(playerCount).fill(gameMode));
    setMatchesWon(Array(playerCount).fill(0));
    setDartsThrown(Array(playerCount).fill(0));
    setThrows(Array.from({ length: playerCount }, () => []));
    setCount180s(Array(playerCount).fill(0));
    setAdvancedStats(createEmptyMatchAdvancedStats());
    setLegAverages([]);
    setInput('');
    setMoveHistory([]);
    setMatchCount(0);
    setActivePlayer(0);
    setFirstStarter(0);
  };

  const handleUndo = () => {
    if (moveHistory.length === 0) return;
    
    setConfirmModal({
      visible: true,
      title: 'Deshacer',
      message: '¿Deshacer la última tirada?',
      onConfirm: () => {
        const lastState = moveHistory[moveHistory.length - 1];
        setScores(lastState.scores);
        setActivePlayer(lastState.activePlayer);
        setDartsThrown(lastState.dartsThrown);
        setThrows(lastState.throws);
        setCount180s(lastState.count180s);
        setAdvancedStats(lastState.advancedStats);
        setInput('');
        setMoveHistory(prev => prev.slice(0, -1));
        setConfirmModal({ visible: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleExit = () => {
    setConfirmModal({
      visible: true,
      title: 'Salir de la partida',
      message: '¿Seguro que quieres salir? Se perderá el progreso.',
      onConfirm: () => {
        clearCelebrationTimers();
        onBack();
      }
    });
  };

  // ========== RENDER ==========

  const numpadKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [input.length > 0 ? '←' : 'BUST', '0', 'OK'],
  ];

  return (
    <div className="x01-game-container">
      {/* Alert */}
      {alertMsg && (
        <div className="x01-alert">
          <span>{alertMsg}</span>
        </div>
      )}

      {/* Header - estilo web */}
      <div className="x01-game-header">
        <button className="x01-back-btn" onClick={handleExit}>← Salir</button>
        <div className="x01-match-info">
          <span className="x01-match-mode">{gameMode}</span>
          <span className="x01-match-legs">Primero a {legsToWin}</span>
        </div>
      </div>

      {/* Marcador de legs */}
      <div className="x01-legs-display">
        {playerNames.map((name, i) => (
          <div key={i} className={`x01-leg-counter ${i === activePlayer ? 'active' : ''}`}>
            <span className="x01-leg-name">{name}</span>
            <span className="x01-leg-count">{matchesWon[i]}</span>
          </div>
        ))}
      </div>

      {/* Game Body - 3 columnas en desktop */}
      <div className="x01-game-body">
        {/* Panel de rondas: Desktop = tabla vertical | Mobile = filas por jugador (como la app) */}
        <div className="x01-rounds-panel">
          {/* === DESKTOP: tabla con cabecera + filas por ronda === */}
          <div className="x01-rounds-desktop">
            <div className="x01-rounds-header">
              <span className="x01-rounds-label">#</span>
              {playerNames.map((name, i) => (
                <span key={i} className={`x01-rounds-player ${i === activePlayer ? 'active' : ''}`}>{name}</span>
              ))}
            </div>
            <div className="x01-rounds-body" ref={roundsPanelRef}>
              {maxRounds > 0 ? (
                Array.from({ length: maxRounds }, (_, roundIdx) => (
                  <div key={roundIdx} className={`x01-round-row ${roundIdx === maxRounds - 1 ? 'latest' : ''}`}>
                    <span className="x01-round-num">{roundIdx + 1}</span>
                    {throws.map((playerThrows, pi) => {
                      const val = playerThrows[roundIdx];
                      const cls = val === 180 ? 'one-eighty' : val >= 100 ? 'ton' : val === 0 ? 'bust' : '';
                      return (
                        <span key={pi} className={`x01-round-score ${cls}`}>
                          {val !== undefined ? (val === 0 ? 'BUST' : val) : ''}
                        </span>
                      );
                    })}
                  </div>
                ))
              ) : (
                <p className="x01-rounds-empty">Sin rondas</p>
              )}
            </div>
          </div>

          {/* === MOBILE: filas por jugador con pills horizontales (como la app) === */}
          <div className="x01-rounds-mobile">
            {playerNames.map((name, pi) => (
              <div key={pi} className="x01-rounds-player-row">
                <div className={`x01-rounds-player-label ${pi === activePlayer ? 'active' : ''}`}>
                  <span>{name}</span>
                </div>
                <div 
                  className="x01-rounds-player-scroll" 
                  ref={el => { mobileScrollRefs.current[pi] = el; }}
                >
                  {throws[pi].map((pts, idx) => {
                    const isLast = idx === throws[pi].length - 1;
                    const cls = pts === 180 ? 'one-eighty' : pts >= 100 ? 'ton' : pts === 0 ? 'bust' : '';
                    return (
                      <span key={idx} className={`x01-rounds-pill ${cls} ${isLast ? 'latest' : ''}`}>
                        R{idx + 1}.{pts === 0 ? 'BUST' : pts}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Panel central: Scores + Input */}
        <div className="x01-center-panel">
          <ScoreCelebrationBanner type={scoreCelebration} />
          <div className="x01-scores-display" ref={scoresDisplayRef}>
            {playerNames.map((name, i) => (
              <div key={i} className={`x01-score-panel ${i === activePlayer ? 'active' : ''}`}>
                <div className="x01-score-number">
                  {scores[i]}
                </div>
                <div className="x01-score-avg">
                  AVG: {currentAverages[i].toFixed(1)}
                </div>
              </div>
            ))}
          </div>

          {/* Input display + undo */}
          <div className="x01-input-section">
            <div className="x01-input-display">
              {input === '' && scores[activePlayer] >= 2 && scores[activePlayer] <= 170 ? (
                <span className="x01-checkout-hint-big">
                  {formatCheckout(getCheckoutOptions(scores[activePlayer])[0] || '')}
                </span>
              ) : (
                <span className="x01-input-value">{input || '\u2014'}</span>
              )}
            </div>
            <button 
              className={`x01-undo-btn ${moveHistory.length === 0 || resultModal.pending ? 'disabled' : ''}`}
              onClick={handleUndo}
              disabled={moveHistory.length === 0 || resultModal.pending}
            >
              ↩ Deshacer
            </button>
          </div>
        </div>

        {/* Panel derecho: Numpad */}
        <div className="x01-right-panel">
          <div className={`x01-numpad ${resultModal.pending ? 'disabled' : ''}`}>
            {numpadKeys.map((row, ri) => (
              <div key={ri} className="x01-numpad-row">
                {row.map(key => {
                  const isBack = key === '←';
                  const isBust = key === 'BUST';
                  const isOK = key === 'OK';
                  let cls = 'x01-numpad-key';
                  if (isBack || isBust) cls += ' clear';
                  if (isOK) cls += ' ok';
                  return (
                    <button
                      key={key}
                      className={cls}
                      onClick={() => {
                        if (isOK) handleOk();
                        else if (isBack) setInput(prev => prev.slice(0, -1));
                        else if (isBust) handleClear();
                        else handleNumpad(key);
                      }}
                    >
                      {key}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sponsors */}
      <SponsorsStrip />

      {/* Keyboard shortcut hint */}
      <div className="x01-keyboard-hint">
        Teclado: 0-9 · Enter = OK · Backspace = Borrar · Ctrl+Z = Deshacer
      </div>

      {/* Modales */}
      <DardosCierreModal
        visible={dardosModal.visible}
        closingScore={dardosModal.closingScore}
        onSelect={handleDardosCierre}
      />

      <TrainingMatchResultModal
        visible={resultModal.visible}
        data={resultModal.data}
        onNextLeg={handleNextLeg}
        onFinish={onBack}
        onRematch={handleRematch}
      />

      <ConfirmModal
        visible={confirmModal.visible}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ visible: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
};

export default X01Game;
